import { useCallback, useState, useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { useAppProperties, useMainTabs, useOffers } from "@/hooks";
import { useResponsiveness, useTranslate, useUser } from "@/providers/context";
import { PageLoader, Switch } from "@/components";
import { CODE_VERSION } from "@/env.ts";
import {
  isAutoplayEnabled,
  isClientScheduledEnabled,
  setClientScheduledEnabled,
  setIsAutoplayEnabled,
  toPascalCase,
} from "@/utils";
import { Header } from "@/app/modules/Navigation";
import { DRAWER_BLOCKED_USERS, DRAWER_NOTIFICATION_SETTINGS } from "@/const.ts";

import { useStripeUser } from "./hooks/useStripeUser.tsx";
import { ProfileHeader } from "./components/ProfileHeader.tsx";
import { Section } from "./components/Section.tsx";
import { ProfileRow } from "./components/ProfileRow.tsx";
import { SignInOutButton } from "./components/SignInOutButton.tsx";
import { YourPlan } from "./components/YourPlan.tsx";
import { getUrl } from "./utils/util.ts";
import { BlockedUsersDrawer } from "./BlockedUsersDrawer.tsx";
import { NotificationSettingsDrawer } from "./NotificationSettingsDrawer.tsx";

export const ProfileScreen = () => {
  const [autoplay, setAutoplay] = useState(isAutoplayEnabled());
  const [clientScheduled, setClientScheduled] = useState(
    isClientScheduledEnabled(),
  );
  const { isLoading: offersLoading } = useOffers();
  const { uid, detailed_products } = useUser();
  const { data: properties, communityEnabled } = useAppProperties();
  const navigate = useNavigate();
  const { translate } = useTranslate();
  const { activeSubscription: stripeSubscription, isLoading: subLoading } =
    useStripeUser();
  const { communityTab } = useMainTabs();
  const { setOpenDrawer, drawerMargin, transitionStyle } = useResponsiveness();
  const isSignedIn = !!uid;

  const onAutoplayChange = useCallback(
    (bool: boolean) => {
      setAutoplay(bool);
      setIsAutoplayEnabled(bool);
    },
    [setAutoplay],
  );
  const onClientScheduledChange = useCallback(
    (bool: boolean) => {
      setClientScheduled(bool);
      setClientScheduledEnabled(bool);
    },
    [setClientScheduled],
  );

  const hasExternalSubscriptions = useMemo(() => {
    return detailed_products?.some(
      (product) => product.platform === "Manual" && !!product.old_platform,
    );
  }, [detailed_products]);

  const showCommunitySections = useMemo(
    () => isSignedIn && !!communityTab && communityEnabled,
    [communityEnabled, communityTab, isSignedIn],
  );

  const notificationSettingsTitle = useMemo(() => {
    const communityName = toPascalCase(
      communityTab?.Name ?? translate("Community"),
    );
    return `${communityName} ${translate("Notification settings")}`;
  }, [communityTab, translate]);

  const showSubscriptionHistory = useMemo(
    () =>
      !stripeSubscription &&
      properties?.FreeAccessApp !== "1" &&
      !hasExternalSubscriptions,
    [stripeSubscription, properties, hasExternalSubscriptions],
  );

  const showAccountSection = useMemo(
    () => showCommunitySections || showSubscriptionHistory,
    [showCommunitySections, showSubscriptionHistory],
  );

  if (!uid) {
    return <Navigate to={`/sign-in`} replace />;
  }

  if (subLoading || offersLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <div
        className="px-7 pb-6"
        style={{ marginRight: drawerMargin, transition: transitionStyle }}
      >
        <Header content={<div></div>} />
        <div className="flex justify-center">
          <div className="flex min-w-[400px] max-w-[624px] flex-1 flex-col items-center">
            <ProfileHeader />
            {properties?.FreeAccessApp !== "1" && <YourPlan />}
            <Section heading={translate("Device Settings")}>
              <div className="flex cursor-pointer justify-between py-[4px]">
                <div>{translate("Allow autoplay of next video")}</div>
                <Switch onCheckedChange={onAutoplayChange} checked={autoplay} />
              </div>
              <div className="flex cursor-pointer justify-between py-[4px]">
                <div>
                  {translate("Allow client scheduled content in Calendar")}
                </div>
                <Switch
                  onCheckedChange={onClientScheduledChange}
                  checked={clientScheduled}
                />
              </div>
            </Section>
            {showAccountSection && (
              <Section heading={translate("Account")}>
                {showSubscriptionHistory && (
                  <ProfileRow
                    name={translate("See Web Subscription History")}
                    disabled={!isSignedIn}
                    onClick={() =>
                      window.open(`/manage-subscription`, "_blank")
                    }
                  />
                )}
                {showCommunitySections && (
                  <>
                    <ProfileRow
                      name={translate("Blocked Users")}
                      onClick={() => setOpenDrawer(DRAWER_BLOCKED_USERS)}
                    />
                    <ProfileRow
                      name={notificationSettingsTitle}
                      onClick={() =>
                        setOpenDrawer(DRAWER_NOTIFICATION_SETTINGS)
                      }
                      className="capitalize"
                    />
                  </>
                )}
              </Section>
            )}
            <Section heading={translate("Help & Links")} hideSeparator>
              <ProfileRow
                name={translate("Need help?")}
                onClick={() =>
                  window.open(
                    getUrl(properties?.AppSupportURL) ||
                      "https://vidapp.com/app-vid-app-user-support",
                  )
                }
              />
              <ProfileRow
                name={translate("Terms & Conditions")}
                onClick={() =>
                  window.open(
                    getUrl(properties?.TermsOfService) ||
                      "https://vidapp.com/terms-and-conditions",
                  )
                }
              />
              <ProfileRow
                name={translate("Data & Privacy")}
                disabled={!isSignedIn}
                onClick={() => navigate("/data-privacy")}
              />
            </Section>
            <SignInOutButton />
            <div className="p2-regular pt-4 text-item-secondary">
              {properties?.HideVidAppInVersionLabel !== "1" && "VidApp"}{" "}
              {CODE_VERSION}
            </div>
          </div>
        </div>
      </div>
      <BlockedUsersDrawer />
      <NotificationSettingsDrawer
        title={notificationSettingsTitle}
        titleClassName="capitalize"
      />
    </>
  );
};
